import { Injectable } from '@angular/core';
import { SysGlobals } from './sysglobals';
import { SessionService } from './session.service';
import { ScreenService } from './screen.service';
import { FieldFormComponent } from '@ui/layouts/fieldlayouts/field-form/fieldform.component';
import { Utils } from '../helpers/Utils';
import Validator from "devextreme/ui/validator";
import { BaseCustomizationElement, ComponentCustomization, FieldCustomizationElement } from '@m4u-angular/base/classes/BaseClasses';
import { DxDataGridComponent } from 'devextreme-angular';

@Injectable()
export class CustomizationService
{
    FieldsCustomizations: { [key: string]: FieldCustomizationElement[] } = {};
    ComponentsCustomizations: { [key: string]: ComponentCustomization[] } = {};

    constructor(private screenService: ScreenService,
        private sessionService: SessionService) {
    }

    /* Components customization */

    SetComponentCustomizations(components: any[], parentComponentName: string) {
        components.forEach(x => {
            let customizations = this.GetComponentCustomizations(x, parentComponentName);
            if (!customizations.length) return;

            if (x instanceof DxDataGridComponent) {
                //Separate customizations for columns and all the grid
                const [ columnsCustomizations, gridCustomizations ] = this.GetSeparatedGridCustomizations(customizations);
                //Hide grid columns
                this.HideGridColumns(x, columnsCustomizations);
                //Set customizations as grid customizations to check if need to hide all the grid
                customizations = gridCustomizations;
            }
            if (this.IsNeedToHideElement(customizations)) x.visible = false;
        });
    }

    GetComponentCustomizations(component: any, parentComponentName: string) {
        const componentName = component.Name;
        //Validate name is set - throw a clear exception to help find the component
        if (!componentName) {
            const componentType = component instanceof DxDataGridComponent
                ? 'data grid' : component.hasOwnProperty('index')
                    ? 'tab' : 'section';
            throw new Error(`You forgot to define a name for some ${componentType} in ${parentComponentName} component`);
        }
        
        const componentKey = `${parentComponentName}_${componentName}`;

        if (!this.ComponentsCustomizations[componentKey]) {
            const fieldCustomizations = SysGlobals.ComponentCustomizations
                ? SysGlobals.ComponentCustomizations
                    .filter(this.FilterCustomizations(parentComponentName, componentName))
                : [];
            this.ComponentsCustomizations[componentKey] = fieldCustomizations;
        }

        return this.ComponentsCustomizations[componentKey];
    }

    GetSeparatedGridCustomizations(customizations: ComponentCustomization[]) {
        return customizations
            .reduce((result, element) => {
                result[!!element.Filter.Columns ? 0 : 1].push(element);
                return result;
            }, [[], []]);
    }

    HideGridColumns(grid: DxDataGridComponent, customizations: ComponentCustomization[]) {
        //Get column names that need to hide
        const columns = grid.columns.filter((column: any) => {
            const columnCustomizations = customizations
                .filter(c => c.Filter.Columns && c.Filter.Columns.find(z => z == column.dataField));
            return this.IsNeedToHideElement(columnCustomizations);
        }).map((x: any) => x.dataField);

        if (columns.length) {
            //Remove columns that need to hide from grid
            let updatedColumns = grid.columns
                .filter((c: any) => columns.indexOf(c.dataField) < 0);
            grid.instance.option('columns', updatedColumns);
        }
    }

    /* Fields customization */

    public SetFieldDefaultValue(fieldComponent: FieldFormComponent)
    {
        //Skip
        const relevantCustomizations = this.GetRelevantCustomizations(fieldComponent);
        if (!relevantCustomizations.length) return;

        //Get default value
        var oDefaults = relevantCustomizations.filter(x => x.Logic.DefaultValue != undefined);
        if (!oDefaults.length) return;

        //Get control
        let control = fieldComponent.GetEditorControl();
        if (control == undefined) return;

        //Set value to control
        if (!control.value) control.value = oDefaults[oDefaults.length - 1].Logic.DefaultValue;
    }

    //Configures the customizations
    public SetFieldCustomizations(fieldComponent: FieldFormComponent)
    {
        //Get customizations
        const relevantCustomizations = this.GetRelevantCustomizations(fieldComponent);
        if (!relevantCustomizations.length) return;

        //Set label
        var oLabels = relevantCustomizations.filter(x => !Utils.IsEmpty(x.Logic.Label));
        if (oLabels.length) fieldComponent.Label = oLabels[oLabels.length - 1].Logic.Label;

        //Get relevant editor control
        //If none exists, the rest of the customizations do not apply
        let control = fieldComponent.GetEditorControl();
        if (control == undefined) return;

        //Get hide lists values customizations
        var oFieldFilters = relevantCustomizations.filter(x => x.Logic.HideValues != undefined);

        //Hide
        if (!oFieldFilters.length && this.IsNeedToHideElement(relevantCustomizations)) fieldComponent.IsHide = true;

        //Hide lists values by text
        if (oFieldFilters.length && control.items && this.IsNeedToHideElement(relevantCustomizations)) {
            control.items = control.items.filter(x => oFieldFilters[oFieldFilters.length - 1].Logic.HideValues.indexOf(x.Text) < 0);
        }

        //Get instance, rules, and validator
        let oElement = control.instance.element();
        let validatorInstance = Validator.getInstance(oElement) as Validator;

        //Make readonly
        if (relevantCustomizations.filter(x => x.Logic.ReadOnly).length > 0) control.readOnly = true;

        //Set mandatory rule
        var oMandatory = relevantCustomizations.filter(x => x.Logic.Required);
        if (oMandatory.length)
        {
            if (!validatorInstance) validatorInstance = new Validator(oElement, {});
            let rules = validatorInstance.option("validationRules");
            rules.push({ type: "required" });
            validatorInstance.option("validationRules", rules);
        }

        //Set custom validation rules
        var oCustomValidations = relevantCustomizations.filter(x => x.Logic.ValidationFunction);
        if (oCustomValidations.length)
        {
            if (!validatorInstance) validatorInstance = new Validator(oElement, {});
            let rules = validatorInstance.option("validationRules") as any[];
            oCustomValidations.forEach(oValidation => rules.push(
                {
                    type: "custom",
                    validationCallback: this.OnFieldValidation,
                    message: oValidation.Logic.ValidationMessage,
                    ValidationFunction: oValidation.Logic.ValidationFunction
                }));
            validatorInstance.option("validationRules", rules);
        }
    }

    OnFieldValidation(e): boolean
    {
        return e.rule.ValidationFunction(e.value, SysGlobals.CurrentEntity, SysGlobals.CurrentState);
    }

    //Returns the relevant customizations for this field
    private GetRelevantCustomizations(fieldComponent: FieldFormComponent): FieldCustomizationElement[]
    {
        //Validate component is set - throw a clear exception to help find the component
        if (fieldComponent.Component == undefined || fieldComponent.Name == undefined)
        {
            throw new Error(`You forgot to define a component binding or name for this field - ${fieldComponent.Name != undefined ? fieldComponent.Name : fieldComponent.Label}`);
        }

        const fieldKey = `${fieldComponent.Component.ComponentStateName}_${fieldComponent.Name}`;

        if (!this.FieldsCustomizations[fieldKey]) {
            const fieldCustomizations = SysGlobals.FieldCustomizations
                ? SysGlobals.FieldCustomizations
                    .filter(this.FilterCustomizations(fieldComponent.Component.ComponentStateName, fieldComponent.Name))
                : [];
            this.FieldsCustomizations[fieldKey] = fieldCustomizations;
        }

        return this.FieldsCustomizations[fieldKey];
    }

    /* Common functions */

    private FilterCustomizations(componentName: string, elementName: string) {
        return x => {
            if (!x.Filter.Component || !x.Filter.Name) return false;

            //Check component
            if (x.Filter.Component != '*')
            {
                if (Array.isArray(x.Filter.Component))
                {
                    if (!x.Filter.Component.find(z => z == componentName)) return false;
                }
                else
                {
                    if (x.Filter.Component != componentName) return false;
                }
            }

            //Check element
            if (x.Filter.Name != '*')
            {
                if (Array.isArray(x.Filter.Name))
                {
                    if (!x.Filter.Name.find(z => z == elementName)) return false;
                }
                else
                {
                    if (x.Filter.Name != elementName) return false;
                }
            }

            //Check user
            if (x.Filter.User && (x.Filter.User as any) != '*')
            {
                if (Array.isArray(x.Filter.User))
                {
                    if (!x.Filter.User.find(z => z == this.sessionService.CurrentUserID)) return false;
                }
                else
                {
                    if (x.Filter.User != this.sessionService.CurrentUserID) return false;
                }
            }

            //If got here - means customization applies here
            return true;
        };
    }

    IsNeedToHideElement(customizations: BaseCustomizationElement[]) {
        return customizations.filter(c => c.Logic.Hide).length
                || (this.screenService.IsDesktop() && customizations.filter(c => c.Logic.HideOnDesktop).length)
                || (this.screenService.IsMobile() && customizations.filter(c => c.Logic.HideOnMobile).length)
    }
}
